import { request } from '@/utils/http'

/**
 * 序列化实体主键：雪花 ID 超过 Number.MAX_SAFE_INTEGER，禁止 Number() 转换
 */
function serializeEntityId(id?: string | null): string | undefined {
  if (id === null || id === undefined || id === '') {
    return undefined
  }
  return String(id)
}

/**
 * 异常规则条件
 */
export interface RuleCondition {
  triggerType: 'timeout' | 'status'
  refNode?: string
  timeType?: string
  timeDirection?: string
  compareBase?: string
  compareTimeType?: string
  thresholdValue?: number
  thresholdUnit?: string
  checkTarget?: string
  checkCondition?: string
  targetNode?: string
  targetStatus?: string
}

export interface RecipientConfig {
  fixed?: string[]
  dynamic?: string[]
  dingtalkWebhook?: string
}

export interface ExceptionRuleVO {
  id: string
  businessCode: string
  ruleCode: string
  ruleName: string
  description: string
  condition: RuleCondition
  alertLevel: number
  notifyChannels: string[]
  recipients: RecipientConfig
  triggerTimes: string[]
  enabled: number
  preset?: number
}

export interface ExceptionRuleSaveRequest {
  id?: string
  businessCode: string
  ruleCode?: string
  ruleName: string
  description?: string
  condition: RuleCondition
  alertLevel: number
  notifyChannels: string[]
  recipients: RecipientConfig
  triggerTimes: string[]
  enabled?: number
}

/** 查询异常规则列表 */
export function queryExceptionRules(params?: {
  businessCode?: string
  enabled?: number
}): Promise<ExceptionRuleVO[]> {
  return request.post('/sys/exception-rule/query', params ?? {})
}

/** 新增或保存异常规则 */
export function saveExceptionRule(data: ExceptionRuleSaveRequest): Promise<ExceptionRuleVO> {
  const payload = {
    ...data,
    id: serializeEntityId(data.id)
  }
  return request.post('/sys/exception-rule/save', payload)
}

/** 删除异常规则 */
export function deleteExceptionRule(id: string): Promise<boolean> {
  return request.post('/sys/exception-rule/delete', { id: serializeEntityId(id) })
}

export interface AlertRecordPopupVO {
  id: string
  alertTitle: string
  waybillNo: string
  waybillId: string
  triggeredAt: string
  alertDescription: string
  alertLevel: number
  totalPending?: number
}

/** 登录弹窗：查询当前用户待处理预警（最多 5 条） */
export function queryPopupAlerts(): Promise<AlertRecordPopupVO[]> {
  return request.post('/sys/exception-rule/alert/popup', {})
}

/** 标记预警已读（稍后处理） */
export function dismissAlerts(alertIds: string[]): Promise<boolean> {
  return request.post('/sys/exception-rule/alert/dismiss', {
    alertIds: alertIds.map(id => serializeEntityId(id)).filter(Boolean),
    status: 'READ'
  })
}

export interface ExceptionRuleCheckResult {
  ruleId: string
  triggeredCount: number
}

/** 手动触发单条规则检测（联调/运维） */
export function checkExceptionRule(ruleId: string): Promise<ExceptionRuleCheckResult> {
  return request.post('/sys/exception-rule/check', { ruleId: serializeEntityId(ruleId) })
}

/** 订单读取范围配置 */
export interface OrderRangeConfig {
  timeBase: 'orderTime' | 'createTime' | 'etdTime'
  startOffset?: number
  endOffset?: number
  includeNoTime: boolean
}

export interface OrderRangeVO extends OrderRangeConfig {
  businessCode?: string
  configSummary?: string
  rangeEffective?: boolean
}

/** 查询订单读取范围 */
export function queryOrderRange(businessCode: string): Promise<OrderRangeVO> {
  return request.post('/sys/exception-rule/order-range/query', { businessCode })
}

/** 保存订单读取范围 */
export function saveOrderRange(
  data: OrderRangeConfig & { businessCode: string }
): Promise<OrderRangeVO> {
  return request.post('/sys/exception-rule/order-range/save', data)
}

/** 重置订单读取范围（恢复全量扫描） */
export function resetOrderRange(businessCode: string): Promise<boolean> {
  return request.post('/sys/exception-rule/order-range/reset', { businessCode })
}

export type BusinessExceptionRuleObjectType = 'TRANSPORT_CONSIGNMENT' | 'WAYBILL' | 'FEE'
export type BusinessExceptionRuleTriggerType = 'TIMEOUT' | 'STATUS'

export interface BusinessExceptionRuleCondition {
  triggerType: BusinessExceptionRuleTriggerType
  timeField?: string
  thresholdValue?: number
  thresholdUnit?: 'HOUR' | 'DAY'
  completionField?: string
  completedValues?: string[]
  statusField?: string
  operator?: 'IN' | 'NOT_IN' | 'EQ'
  values?: string[]
}

export interface BusinessExceptionRuleMetadata {
  objectType: BusinessExceptionRuleObjectType
  conditionTypes: BusinessExceptionRuleTriggerType[]
  timeFields: string[]
  statusFields: string[]
  statusValueOptions: Record<string, string[]>
  operators: Array<'IN' | 'NOT_IN' | 'EQ'>
  thresholdUnits: Array<'HOUR' | 'DAY'>
}

export interface BusinessExceptionRuleVO {
  id: string
  ruleScene: number
  businessCode: string
  ruleCode?: string
  ruleName: string
  description?: string
  condition: BusinessExceptionRuleCondition
  notifyChannels: string[]
  recipients: RecipientConfig
  triggerTimes: string[]
  enabled: number
  objectType: BusinessExceptionRuleObjectType
  categoryCode: string
  subcategoryCode: string
  levelCode: string
  assigneeIds: string[]
  cooldownHours?: number
}

export interface BusinessExceptionRuleSaveRequest {
  id?: string
  businessCode: string
  ruleCode?: string
  ruleName: string
  description?: string
  condition: BusinessExceptionRuleCondition
  notifyChannels: string[]
  recipients: RecipientConfig
  triggerTimes: string[]
  enabled: number
  objectType: BusinessExceptionRuleObjectType
  categoryCode: string
  subcategoryCode: string
  levelCode: string
  assigneeIds: string[]
  cooldownHours?: number
}

export function queryBusinessExceptionRules(): Promise<BusinessExceptionRuleVO[]> {
  return request.post('/sys/exception-rule/query', { ruleScene: 2 })
}

export function getBusinessExceptionRuleMetadata(): Promise<BusinessExceptionRuleMetadata[]> {
  return request.post('/sys/exception-rule/metadata', {})
}

export function saveBusinessExceptionRule(data: BusinessExceptionRuleSaveRequest): Promise<BusinessExceptionRuleVO> {
  return request.post('/sys/exception-rule/save', {
    ...data,
    id: serializeEntityId(data.id),
    ruleScene: 2,
    assigneeIds: data.assigneeIds.map(id => String(id))
  })
}

export function deleteBusinessExceptionRule(id: string): Promise<boolean> {
  return request.post('/sys/exception-rule/delete', { id: serializeEntityId(id) })
}

